import { useState, useEffect } from "react";
import { Cookie, X, ChevronDown, ChevronUp, ShieldCheck, BarChart3 } from "lucide-react";
import { Link } from "react-router-dom";
import { getConsent, setConsent, applyConsent } from "../lib/consent";

/**
 * Cookie consent banner — shown on first visit.
 * Can be re-opened via the "Cookie-indstillinger" button in the footer
 * (parent passes `forceOpen` prop).
 */
export default function CookieConsent({ forceOpen = false, onClose }) {
    const [open, setOpen] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [analytics, setAnalytics] = useState(false);

    useEffect(() => {
        if (forceOpen) {
            const current = getConsent();
            setAnalytics(current?.analytics ?? false);
            setOpen(true);
            return;
        }
        // Show banner only on first visit
        const t = setTimeout(() => {
            const existing = getConsent();
            if (!existing) setOpen(true);
        }, 600);
        return () => clearTimeout(t);
    }, [forceOpen]);

    const close = () => {
        setOpen(false);
        setShowSettings(false);
        if (onClose) onClose();
    };

    const acceptAll = () => {
        setConsent({ analytics: true });
        applyConsent({ statistics: true, marketing: true });
        close();
    };

    const rejectAll = () => {
        setConsent({ analytics: false });
        applyConsent({ statistics: false, marketing: false });
        close();
    };

    const saveCustom = () => {
        setConsent({ analytics });
        // Banneret har ingen marketing-kategori i UI'en, så marketing forbliver
        // "denied" ved brugerdefineret gem. Kun "Accepter alle" kan give marketing consent.
        applyConsent({ statistics: analytics, marketing: false });
        close();
    };

    if (!open) return null;

    return (
        <div
            className="fixed inset-x-0 bottom-0 z-[120] p-3 sm:p-5 pointer-events-none"
            role="dialog"
            aria-labelledby="cookie-title"
            aria-describedby="cookie-desc"
            data-testid="cookie-consent"
        >
            <div className="pointer-events-auto mx-auto max-w-5xl rounded-2xl bg-white border border-slate-200 shadow-2xl shadow-slate-900/15 overflow-hidden">
                {/* Header */}
                <div className="flex items-start gap-4 px-5 sm:px-7 pt-5 sm:pt-6">
                    <div className="h-10 w-10 rounded-full bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 shrink-0">
                        <Cookie className="h-5 w-5" strokeWidth={1.75} />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 id="cookie-title" className="font-display text-base md:text-lg font-bold text-slate-900">
                            Vi bruger cookies
                        </h2>
                        <p id="cookie-desc" className="mt-1 text-[13px] text-slate-600 leading-relaxed">
                            Vi bruger nødvendige cookies for at siden virker, og statistik-cookies for at forstå hvordan
                            siden bruges. Du kan altid ændre dit valg via{" "}
                            <span className="font-semibold text-slate-700">Cookie-indstillinger</span> i footeren.{" "}
                            <Link to="/cookies" className="text-sky-700 hover:text-sky-900 underline underline-offset-2 font-medium">
                                Læs cookie-politik
                            </Link>
                        </p>
                    </div>
                    {!forceOpen && (
                        <button
                            type="button"
                            onClick={rejectAll}
                            aria-label="Luk og afvis alle"
                            data-testid="cookie-close"
                            className="shrink-0 -mr-2 -mt-1 h-8 w-8 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-slate-700 hover:bg-slate-100"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                {/* Settings (collapsible) */}
                {showSettings && (
                    <div className="px-5 sm:px-7 mt-4 grid sm:grid-cols-2 gap-3" data-testid="cookie-settings">
                        <CategoryRow
                            icon={ShieldCheck}
                            title="Nødvendige"
                            desc="Holder siden funktionel — kan ikke fravælges."
                            checked
                            disabled
                            testid="cookie-cat-necessary"
                        />
                        <CategoryRow
                            icon={BarChart3}
                            title="Statistik (PostHog)"
                            desc="Hjælper os med at forstå hvordan siden bruges, så vi kan forbedre den."
                            checked={analytics}
                            onChange={setAnalytics}
                            testid="cookie-cat-analytics"
                        />
                    </div>
                )}

                {/* Buttons */}
                <div className="px-5 sm:px-7 pt-4 sm:pt-5 pb-5 sm:pb-6 flex flex-col-reverse sm:flex-row sm:items-center sm:justify-between gap-2">
                    <button
                        type="button"
                        onClick={() => setShowSettings((v) => !v)}
                        data-testid="cookie-toggle-settings"
                        className="inline-flex items-center gap-1.5 text-[13px] font-semibold text-slate-700 hover:text-slate-900 self-start sm:self-auto"
                    >
                        {showSettings ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
                        Tilpas valg
                    </button>
                    <div className="flex flex-col sm:flex-row gap-2">
                        {showSettings ? (
                            <button
                                type="button"
                                onClick={saveCustom}
                                data-testid="cookie-save"
                                className="inline-flex items-center justify-center rounded-full bg-slate-900 hover:bg-slate-800 text-white font-semibold text-sm h-10 px-5"
                            >
                                Gem valg
                            </button>
                        ) : (
                            <button
                                type="button"
                                onClick={rejectAll}
                                data-testid="cookie-reject"
                                className="inline-flex items-center justify-center rounded-full border border-slate-300 hover:border-slate-400 bg-white text-slate-800 font-medium text-sm h-10 px-5"
                            >
                                Afvis alle
                            </button>
                        )}
                        <button
                            type="button"
                            onClick={acceptAll}
                            data-testid="cookie-accept"
                            className="inline-flex items-center justify-center rounded-full bg-sky-600 hover:bg-sky-700 text-white font-semibold text-sm h-10 px-5 shadow-md shadow-sky-600/20"
                        >
                            Accepter alle
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
}

function CategoryRow({ icon: Icon, title, desc, checked, disabled = false, onChange, testid }) {
    return (
        <label
            className={`flex items-start gap-3 rounded-xl border p-3 ${
                disabled ? "border-slate-200 bg-slate-50/60" : "border-slate-200 hover:border-sky-300 cursor-pointer"
            }`}
            data-testid={testid}
        >
            <div className="h-9 w-9 rounded-lg bg-sky-50 border border-sky-100 flex items-center justify-center text-sky-600 shrink-0">
                <Icon className="h-4 w-4" strokeWidth={1.75} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-slate-900">{title}</span>
                    {disabled && (
                        <span className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-1.5 py-0.5">
                            Altid aktiv
                        </span>
                    )}
                </div>
                <div className="mt-0.5 text-[12px] text-slate-600 leading-snug">{desc}</div>
            </div>
            <input
                type="checkbox"
                className="mt-1 h-4 w-4 accent-sky-600 cursor-pointer disabled:cursor-not-allowed"
                checked={!!checked}
                disabled={disabled}
                onChange={(e) => onChange && onChange(e.target.checked)}
                aria-label={title}
            />
        </label>
    );
}
